import { API_BASE_URL as API_BASE } from '@/lib/apiOrigin';

/**
 * Centralized endpoint URL builder.
 * All API paths defined here for easy maintenance and search.
 */
export const ENDPOINTS = {
    // Auth
    AUTH: {
        LOGIN: `${API_BASE}/auth/login`,
        REGISTER: `${API_BASE}/auth/register`,
        LOGOUT: `${API_BASE}/auth/logout`,
        REFRESH: `${API_BASE}/auth/refreshToken`,
        FORGOT_PASSWORD: `${API_BASE}/auth/forgot-password`,
        RESET_PASSWORD: `${API_BASE}/auth/reset-password`,
        ME: `${API_BASE}/auth/me`,
        CHANGE_PASSWORD: `${API_BASE}/auth/change-password`,
        UPDATE_PROFILE: `${API_BASE}/auth/update-profile`,
    },


    ACCOUNTS: {
        LIST: `${API_BASE}/accounts`,
        CREATE: `${API_BASE}/accounts`,
        DETAIL: (id) => `${API_BASE}/accounts/${id}`,
        ROLES: (id) => `${API_BASE}/accounts/${id}/roles`,
        MEMBER_ROLE: (id) => `${API_BASE}/accounts/${id}/member-role`,
    },

    // Users
    USERS: {
        LIST: `${API_BASE}/users`,
        SEARCH: `${API_BASE}/users/search`,
        SEARCH_NO_ROLE: `${API_BASE}/users/search/no-role`,
        ADMIN_USERS: `${API_BASE}/users/admin`,
        EXECUTIVE_USERS: `${API_BASE}/users/executive`,
        TASKFORCE_USERS: `${API_BASE}/users/taskforce`,
        PILLAR_USERS: `${API_BASE}/users/pillar`,
        PROJECTMANAGER_USERS: `${API_BASE}/users/projectmanager`,
        MEMBER_USERS: `${API_BASE}/users/member`,
        /** Directory profile only (no roles — use ROLES). */
        DETAIL: (id) => `${API_BASE}/users/${id}`,
        /** Role id + name only; requires MANAGE_USERS. */
        ROLES: (id) => `${API_BASE}/users/${id}/roles`,
        UPDATE: (id) => `${API_BASE}/users/updateUser/${id}`,
        DEACTIVATE: (id) => `${API_BASE}/users/deactivate/${id}`,
        RESET_PASSWORD: `${API_BASE}/users/reset-password`,
    },


    // Workstreams
    WORKSTREAMS: {
        LIST: `${API_BASE}/workstreams`,
        DETAIL: (id) => `${API_BASE}/workstreams/${id}`,
        CREATE: `${API_BASE}/workstreams`,
        UPDATE: (id) => `${API_BASE}/workstreams/${id}`,
        DELETE: (id) => `${API_BASE}/workstreams/${id}`,
    },

    // Dashboard
    DASHBOARD: {
        STATS: `${API_BASE}/dashboard/stats`,
        RECENT_ACTIVITY: `${API_BASE}/dashboard/activity`,
        /** Task-force project rollup; default is summary (no per-task rows). Append `?detail=1` for full tasks. */
        TASK_FORCE_PROJECTS: (taskForceId) =>
            `${API_BASE}/dashboard/task-forces/${taskForceId}/dashboard/projects`,
        PROJECT_MANAGER_SUMMARY: `${API_BASE}/dashboard/project-manager/summary`,
        PROJECT_MANAGER_WORK_CONTEXT: `${API_BASE}/dashboard/project-manager/work-context`,
        PROJECT_MANAGER_WEEKLY_REPORT: `${API_BASE}/dashboard/project-manager/weekly-report`,
    },

    /** Executive-only API (`/api/v1/executive/...`). */
    EXECUTIVE: {
        DASHBOARD: `${API_BASE}/executive/dashboard`,
        AI_SUMMARY: `${API_BASE}/executive/ai-summary`,
        TASK_FORCE_AI_SUMMARY: (taskForceId) =>
            `${API_BASE}/executive/task-forces/${taskForceId}/ai-summary`,
        PILLAR_AI_SUMMARY: (taskForceId, pillarId) =>
            `${API_BASE}/executive/task-forces/${taskForceId}/pillars/${pillarId}/ai-summary`,
    },

    /**
     * Admin portal only (`/api/v1/admin/...`). Other roles keep using legacy paths via `leadershipService`.
     */
    ADMIN: {
        DASHBOARD_SUMMARY: `${API_BASE}/admin/dashboard/summary`,
        USERS: {
            LIST: `${API_BASE}/admin/users`,
            SEARCH: `${API_BASE}/admin/users/search`,
            SEARCH_NO_ROLE: `${API_BASE}/admin/users/search/no-role`,
            ADMIN_USERS: `${API_BASE}/admin/users/admin`,
            EXECUTIVE_USERS: `${API_BASE}/admin/users/executive`,
            TASKFORCE_USERS: `${API_BASE}/admin/users/taskforce`,
            PILLAR_USERS: `${API_BASE}/admin/users/pillar`,
            PROJECTMANAGER_USERS: `${API_BASE}/admin/users/projectmanager`,
            MEMBER_USERS: `${API_BASE}/admin/users/member`,
            RESET_PASSWORD: `${API_BASE}/admin/users/reset-password`,
            DETAIL: (id) => `${API_BASE}/admin/users/${id}`,
            ROLES: (id) => `${API_BASE}/admin/users/${id}/roles`,
            UPDATE: (id) => `${API_BASE}/admin/users/updateUser/${id}`,
            DEACTIVATE: (id) => `${API_BASE}/admin/users/deactivate/${id}`,
        },
        ROLES: {
            LIST: `${API_BASE}/admin/roles`,
            PERMISSIONS_CATALOG: `${API_BASE}/admin/roles/permissions`,
            DETAIL: (id) => `${API_BASE}/admin/roles/${id}`,
            CREATE: `${API_BASE}/admin/roles`,
            UPDATE: (id) => `${API_BASE}/admin/roles/${id}`,
            DELETE: (id) => `${API_BASE}/admin/roles/${id}`,
        },
        ACCOUNTS: {
            LIST: `${API_BASE}/admin/accounts`,
            CREATE: `${API_BASE}/admin/accounts`,
            DETAIL: (id) => `${API_BASE}/admin/accounts/${id}`,
            ROLES: (id) => `${API_BASE}/admin/accounts/${id}/roles`,
        },
        CITIES: {
            LIST: `${API_BASE}/admin/cities`,
            SEARCH: `${API_BASE}/admin/cities/search`,
            DETAIL: (id) => `${API_BASE}/admin/cities/${id}`,
            CREATE: `${API_BASE}/admin/cities`,
            UPDATE: (id) => `${API_BASE}/admin/cities/${id}`,
            DELETE: (id) => `${API_BASE}/admin/cities/${id}`,
        },
        ORGANIZATIONS: {
            LIST: `${API_BASE}/admin/organizations`,
            SEARCH: `${API_BASE}/admin/organizations/search`,
            DETAIL: (id) => `${API_BASE}/admin/organizations/${id}`,
            CREATE: `${API_BASE}/admin/organizations`,
            UPDATE: (id) => `${API_BASE}/admin/organizations/${id}`,
            DELETE: (id) => `${API_BASE}/admin/organizations/${id}`,
        },
        TASK_FORCES: {
            LIST: `${API_BASE}/admin/task-forces`,
            DETAIL: (id) => `${API_BASE}/admin/task-forces/${id}`,
            CREATE: `${API_BASE}/admin/task-forces`,
            UPDATE: (id) => `${API_BASE}/admin/task-forces/${id}`,
            DELETE: (id) => `${API_BASE}/admin/task-forces/${id}`,
            ASSIGN: (id) => `${API_BASE}/admin/task-forces/${id}/assign`,
            // A task force may have any number of chairs, all with equal authority.
            CHAIRS: (id) => `${API_BASE}/admin/task-forces/${id}/chairs`,
            REMOVE_CHAIR: (id, accountId) =>
                `${API_BASE}/admin/task-forces/${id}/chairs/${accountId}`,
        },
        AUDIT_LOGS: {
            HTTP: `${API_BASE}/admin/audit-logs/http`,
            TRANSACTIONS: `${API_BASE}/admin/audit-logs/transactions`,
        },
        NOTIFICATION_CONFIGS: `${API_BASE}/admin/notification-configs`,
        NOTIFICATION_CONFIG_ESCALATION: `${API_BASE}/admin/notification-configs/escalation`,
        NOTIFICATION_CONFIG_ROLE: (kind, roleId) =>
            `${API_BASE}/admin/notification-configs/${kind}/${roleId}`,
    },

    /** Admin-only bulk import (JSON). */
    IMPORT: {
        USERS: `${API_BASE}/import/users`,
        ORGANIZATIONS: `${API_BASE}/import/organizations`,
        TASKS: `${API_BASE}/import/tasks`,
    },

    /**
     * Role-scoped report export (`/api/v1/export/...`). Available to every role.
     *
     * Generation is asynchronous: POST REPORT returns a job id, REPORT_STATUS is
     * polled until it completes, and REPORT_DOWNLOAD streams the finished PDF. A
     * full report makes several AI calls and renders a paginated document, which
     * runs well past any request timeout for a portfolio-wide scope.
     */
    EXPORT: {
        SCOPE: `${API_BASE}/export/scope`,
        REPORT_PREVIEW: `${API_BASE}/export/report/preview`,
        REPORT: `${API_BASE}/export/report`,
        REPORT_STATUS: (jobId) => `${API_BASE}/export/report/${jobId}`,
        REPORT_DOWNLOAD: (jobId) => `${API_BASE}/export/report/${jobId}/download`,
    },

    /** Auto-escalation policy (`/api/v1/auto-escalation`). Admin-only. */
    AUTO_ESCALATION: {
        CONFIG: `${API_BASE}/auto-escalation/config`,
        RUN: `${API_BASE}/auto-escalation/run`,
    },

    /**
     * The single org-wide event countdown (`/api/v1/countdown`). One resource,
     * no id: GET is open to every account, POST/PUT/DELETE are Admin only.
     */
    COUNTDOWN: {
        ROOT: `${API_BASE}/countdown`,
    },

    /**
     * The operational dashboard (`/api/v1/operational-dashboard`). The dashboard
     * and its drill-down need a per-account grant; MY_ACCESS answers a boolean
     * about the caller and is open to anyone signed in; ACCESS is Admin only.
     */
    OPERATIONAL_DASHBOARD: {
        ROOT: `${API_BASE}/operational-dashboard`,
        ENTRIES: (id) => `${API_BASE}/operational-dashboard/indicators/${id}/entries`,
        MY_ACCESS: `${API_BASE}/operational-dashboard/access/me`,
        ACCESS: `${API_BASE}/operational-dashboard/access`,
    },

    /**
     * Operational indicators (`/api/v1/operational-indicators`). Defining one is
     * Admin only; MINE and VALUE are open to whoever is assigned to it.
     */
    OPERATIONAL_INDICATORS: {
        ROOT: `${API_BASE}/operational-indicators`,
        MINE: `${API_BASE}/operational-indicators/my`,
        DETAIL: (id) => `${API_BASE}/operational-indicators/${id}`,
        ASSIGNEES: (id) => `${API_BASE}/operational-indicators/${id}/assignees`,
        /** Append-only reports. A figure is an entry, never a field. */
        ENTRIES: (id) => `${API_BASE}/operational-indicators/${id}/entries`,
        ENTRY: (id, entryId) =>
            `${API_BASE}/operational-indicators/${id}/entries/${entryId}`,
    },

    /** Org structure graph (`/api/v1/structure`), rooted at the caller's access. */
    STRUCTURE: {
        TREE: `${API_BASE}/structure`,
        PROJECT_TASKS: (projectId) => `${API_BASE}/structure/projects/${projectId}/tasks`,
    },

    /** Task force chair (`/api/v1/task-force-chair/...`). Legacy `/task-forces` still served. */
    TASK_FORCES: {
        LIST: `${API_BASE}/task-force-chair`,
        DETAIL: (id) => `${API_BASE}/task-force-chair/${id}`,
        CREATE: `${API_BASE}/task-force-chair`,
        UPDATE: (id) => `${API_BASE}/task-force-chair/${id}`,
        DELETE: (id) => `${API_BASE}/task-force-chair/${id}`,
        MY: `${API_BASE}/task-force-chair/my`,
        // A task force may have any number of chairs, all with equal authority.
        CHAIRS: (id) => `${API_BASE}/task-force-chair/${id}/chairs`,
        REMOVE_CHAIR: (id, accountId) =>
            `${API_BASE}/task-force-chair/${id}/chairs/${accountId}`,
        MY_AI_SUMMARY: `${API_BASE}/task-force-chair/my/ai-summary`,
        DASHBOARD: (id) => `${API_BASE}/task-force-chair/${id}/dashboard`,
        PILLAR_COUNT: (id) => `${API_BASE}/task-force-chair/${id}/pillars/count`,
        PILLAR_DASHBOARD_SUMMARY: (id) =>
            `${API_BASE}/task-force-chair/${id}/pillar-dashboard-summary`,
        TASK_VELOCITY: (id) => `${API_BASE}/task-force-chair/${id}/velocity/task`,
        TASK_DISTRIBUTION: (id) => `${API_BASE}/task-force-chair/${id}/distribution/task`,
        ASSIGN_ACCOUNT: (id) => `${API_BASE}/task-force-chair/${id}/assign`,
        STATUS: (id) => `${API_BASE}/task-force-chair/${id}/status`,
    },

    /** Pillar chair — “my pillar” and pillar CRUD under `/api/v1/pillar-chair/...`. */
    PILLARS: {
        MY: `${API_BASE}/pillar-chair/pillars/my`,
        MY_DASHBOARD_SUMMARY: `${API_BASE}/pillar-chair/pillars/my/dashboard-summary`,
        MY_AI_SUMMARY: `${API_BASE}/pillar-chair/pillars/my/ai-summary`,
        MY_WEEKLY_REPORT: `${API_BASE}/pillar-chair/pillars/my/weekly-report`,
        MY_PROJECTS: `${API_BASE}/project-manager/projects/my`,
    },

    /**
     * Backend hierarchy: /taskforce/:taskForceId/pillar/.../project/.../task/...
     */
    HIERARCHY: {
        PILLARS: {
            LIST: (taskForceId) => `${API_BASE}/pillar-chair/taskforce/${taskForceId}/pillar`,
            DETAIL: (taskForceId, pillarId) =>
                `${API_BASE}/pillar-chair/taskforce/${taskForceId}/pillar/${pillarId}`,
            CREATE: (taskForceId) => `${API_BASE}/pillar-chair/taskforce/${taskForceId}/pillar`,
            UPDATE: (taskForceId, pillarId) =>
                `${API_BASE}/pillar-chair/taskforce/${taskForceId}/pillar/${pillarId}`,
            DELETE: (taskForceId, pillarId) =>
                `${API_BASE}/pillar-chair/taskforce/${taskForceId}/pillar/${pillarId}`,
            ASSIGN_CHAIR: (taskForceId, pillarId) =>
                `${API_BASE}/pillar-chair/taskforce/${taskForceId}/pillar/${pillarId}/assign`,
            /** Add (POST) / list (GET) chairs for a pillar. */
            CHAIR: (taskForceId, pillarId) =>
                `${API_BASE}/pillar-chair/taskforce/${taskForceId}/pillar/${pillarId}/chair`,
            /** Remove one chair from a pillar. */
            REMOVE_CHAIR: (taskForceId, pillarId, accountId) =>
                `${API_BASE}/pillar-chair/taskforce/${taskForceId}/pillar/${pillarId}/chair/${accountId}`,
            ASSIGN_CITY: (taskForceId, pillarId, cityId) =>
                `${API_BASE}/pillar-chair/taskforce/${taskForceId}/pillar/${pillarId}/city/${cityId}/assign`,
            REMOVE_CITY: (taskForceId, pillarId, cityId) =>
                `${API_BASE}/pillar-chair/taskforce/${taskForceId}/pillar/${pillarId}/city/${cityId}/remove`,
        },
        PROJECTS: {
            LIST: (taskForceId, pillarId) =>
                `${API_BASE}/project-manager/taskforce/${taskForceId}/pillar/${pillarId}/project`,
            VELOCITY: (taskForceId, pillarId) =>
                `${API_BASE}/project-manager/taskforce/${taskForceId}/pillar/${pillarId}/project/velocity`,
            CREATE: (taskForceId, pillarId) =>
                `${API_BASE}/project-manager/taskforce/${taskForceId}/pillar/${pillarId}/project`,
            DETAIL: (taskForceId, pillarId, projectId) =>
                `${API_BASE}/project-manager/taskforce/${taskForceId}/pillar/${pillarId}/project/${projectId}`,
            UPDATE: (taskForceId, pillarId, projectId) =>
                `${API_BASE}/project-manager/taskforce/${taskForceId}/pillar/${pillarId}/project/${projectId}`,
            DELETE: (taskForceId, pillarId, projectId) =>
                `${API_BASE}/project-manager/taskforce/${taskForceId}/pillar/${pillarId}/project/${projectId}`,
            ASSIGN: (taskForceId, pillarId, projectId) =>
                `${API_BASE}/project-manager/taskforce/${taskForceId}/pillar/${pillarId}/project/${projectId}/assign`,
        },
        TASKS: {
            LIST: (taskForceId, pillarId, projectId) =>
                `${API_BASE}/taskforce/${taskForceId}/pillar/${pillarId}/project/${projectId}/task`,
            DETAIL: (taskForceId, pillarId, projectId, taskId) =>
                `${API_BASE}/taskforce/${taskForceId}/pillar/${pillarId}/project/${projectId}/task/${taskId}`,
            CREATE: (taskForceId, pillarId, projectId) =>
                `${API_BASE}/taskforce/${taskForceId}/pillar/${pillarId}/project/${projectId}/task`,
            UPDATE: (taskForceId, pillarId, projectId, taskId) =>
                `${API_BASE}/taskforce/${taskForceId}/pillar/${pillarId}/project/${projectId}/task/${taskId}`,
            DELETE: (taskForceId, pillarId, projectId, taskId) =>
                `${API_BASE}/taskforce/${taskForceId}/pillar/${pillarId}/project/${projectId}/task/${taskId}`,
            ASSIGN: (taskForceId, pillarId, projectId, taskId) =>
                `${API_BASE}/taskforce/${taskForceId}/pillar/${pillarId}/project/${projectId}/task/${taskId}/assign`,
            ASSIGNED_USERS: (taskForceId, pillarId, projectId, taskId) =>
                `${API_BASE}/taskforce/${taskForceId}/pillar/${pillarId}/project/${projectId}/task/${taskId}/assign`,
            REMOVE_ASSIGNEE: (taskForceId, pillarId, projectId, taskId, userId) =>
                `${API_BASE}/taskforce/${taskForceId}/pillar/${pillarId}/project/${projectId}/task/${taskId}/remove/${userId}`,
            CHECKPOINTS: (taskForceId, pillarId, projectId, taskId) =>
                `${API_BASE}/taskforce/${taskForceId}/pillar/${pillarId}/project/${projectId}/task/${taskId}/checkpoints`,
            CHECKPOINT: (taskForceId, pillarId, projectId, taskId, checkpointId) =>
                `${API_BASE}/taskforce/${taskForceId}/pillar/${pillarId}/project/${projectId}/task/${taskId}/checkpoints/${checkpointId}`,
            MESSAGES: (taskForceId, pillarId, projectId, taskId) =>
                `${API_BASE}/taskforce/${taskForceId}/pillar/${pillarId}/project/${projectId}/task/${taskId}/message`,
            INPROGRESS_VERIFY: (taskForceId, pillarId, projectId, taskId, inProgressId) =>
                `${API_BASE}/taskforce/${taskForceId}/pillar/${pillarId}/project/${projectId}/task/${taskId}/inprogress/${inProgressId}/verify`,
            INPROGRESS_REJECT: (taskForceId, pillarId, projectId, taskId, inProgressId) =>
                `${API_BASE}/taskforce/${taskForceId}/pillar/${pillarId}/project/${projectId}/task/${taskId}/inprogress/${inProgressId}/reject`,
        },
        PROJECT_LOCATIONS: {
            LIST: (taskForceId, pillarId) =>
                `${API_BASE}/project-manager/taskforce/${taskForceId}/pillar/${pillarId}/location`,
            CREATE: (taskForceId, pillarId) =>
                `${API_BASE}/project-manager/taskforce/${taskForceId}/pillar/${pillarId}/location`,
            DETAIL: (taskForceId, pillarId, locationId) =>
                `${API_BASE}/project-manager/taskforce/${taskForceId}/pillar/${pillarId}/location/${locationId}`,
            UPDATE: (taskForceId, pillarId, locationId) =>
                `${API_BASE}/project-manager/taskforce/${taskForceId}/pillar/${pillarId}/location/${locationId}`,
            DELETE: (taskForceId, pillarId, locationId) =>
                `${API_BASE}/project-manager/taskforce/${taskForceId}/pillar/${pillarId}/location/${locationId}`,
        },
    },

    // Roles (RBAC)
    ROLES: {
        LIST: `${API_BASE}/roles`,
        PERMISSIONS_CATALOG: `${API_BASE}/roles/permissions`,
        DETAIL: (id) => `${API_BASE}/roles/${id}`,
        CREATE: `${API_BASE}/roles`,
        UPDATE: (id) => `${API_BASE}/roles/${id}`,
        DELETE: (id) => `${API_BASE}/roles/${id}`,
        PERMISSIONS: (id) => `${API_BASE}/roles/${id}/permissions`,
    },

    // Cities
    CITIES: {
        LIST: `${API_BASE}/cities`,
        SEARCH: `${API_BASE}/cities/search`,
        DETAIL: (id) => `${API_BASE}/cities/${id}`,
        CREATE: `${API_BASE}/cities`,
        UPDATE: (id) => `${API_BASE}/cities/${id}`,
        DELETE: (id) => `${API_BASE}/cities/${id}`,
    },

    // Organizations
    ORGANIZATIONS: {
        LIST: `${API_BASE}/organizations`,
        SEARCH: `${API_BASE}/organizations/search`,
        DETAIL: (id) => `${API_BASE}/organizations/${id}`,
        CREATE: `${API_BASE}/organizations`,
        UPDATE: (id) => `${API_BASE}/organizations/${id}`,
        DELETE: (id) => `${API_BASE}/organizations/${id}`,
    },

    TASKS: {
        LIST: `${API_BASE}/tasks`,
        DETAIL: (id) => `${API_BASE}/tasks/${id}`,
        CREATE: `${API_BASE}/tasks`,
        STATS: `${API_BASE}/tasks/stats`,
    },

    /** Member role (`/api/v1/member/...`). Legacy flat `/tasks/my` still served. */
    MY: {
        OVERALL_VELOCITY: `${API_BASE}/member/my/overallvelocity`,
    },

    /** Member task scope (assigned tasks for JWT user) */
    TASKS_MY: {
        LIST: `${API_BASE}/member/tasks/my`,
        STATS: `${API_BASE}/member/tasks/my/stats`,
        PROJECTS: `${API_BASE}/member/tasks/my/projects`,
        DETAIL: (taskId) => `${API_BASE}/member/tasks/my/${taskId}`,
        MESSAGES: (taskId) => `${API_BASE}/member/tasks/my/${taskId}/messages`,
        PATCH: (taskId) => `${API_BASE}/member/tasks/my/${taskId}`,
        SUBMISSIONS: (taskId) => `${API_BASE}/member/tasks/my/${taskId}/submissions`,
        CHECKPOINTS: (taskId) => `${API_BASE}/member/tasks/my/${taskId}/checkpoints`,
    },

    /** MinIO-backed uploads (bucket cop32: profile/, evidence/) */
    STORAGE: {
        PROFILE_PICTURE: `${API_BASE}/storage/profile-picture`,
        EVIDENCE: `${API_BASE}/storage/evidence`,
        PRESIGN: `${API_BASE}/storage/presign`,
    },

    /** Messaging (future backend) */
    CHAT: {
        CONVERSATIONS: `${API_BASE}/chat/conversations`,
        CONVERSATION_MESSAGES: (conversationId) => `${API_BASE}/chat/conversations/${conversationId}/messages`,
        MARK_READ: (conversationId) => `${API_BASE}/chat/conversations/${conversationId}/read`,
        SEARCH_PILLARS: `${API_BASE}/chat/search/pillars`,
        SEARCH_TASKFORCES: `${API_BASE}/chat/search/taskforces`,
        DIRECT: `${API_BASE}/chat/direct`,
    },

    /** Agentic AI assistant. POST messages streams SSE, so it is not fetched via axios. */
    AI_ASSISTANT: {
        CONVERSATIONS: `${API_BASE}/ai-assistant/conversations`,
        CONVERSATION: (id) => `${API_BASE}/ai-assistant/conversations/${id}`,
        MESSAGES: (id) => `${API_BASE}/ai-assistant/conversations/${id}/messages`,
    },
};
